/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ParameterTypes } from "@cxp-shared/contracts";
import { ConditionalToken } from "../Constants/ConditionalToken";
import { DataSources } from "../Constants/DataSources";
import { ListToken } from "../Constants/ListToken";
import { PlaceholderToken } from "../Constants/PlaceholderToken";
import { extractSourceTypeFromBindingItem } from "./TypeExtractor";
import { TargetEntitiesExtractorService } from "../TargetAudience/TargetEntitiesExtractor";
var conditionalPlaceholderReservedWords = [ConditionalToken.If, ConditionalToken.ElseIf, ConditionalToken.Else, ConditionalToken.ClosingIf];
var listPlaceholderReservedWords = [ListToken.Each, ListToken.ClosingEach];
var firstAllowedLetter = /\p{L}/gu; //u is for unicode support
var allowedCharacters = /[\p{L}_[0-9]+/gu;
var disallowedCharacters = /[^\p{L}_[0-9]+/gu;
var maxLoops = 100;
var PlaceholderUtils = /** @class */ (function () {
    function PlaceholderUtils() {
    }
    PlaceholderUtils.getPlaceholderKeyFromPlaceholder = function (placeholderText) {
        var placeholderKey = PlaceholderUtils.getPlaceholderKeyFromText(placeholderText);
        if (!PlaceholderUtils.isPlaceholderKeyValid(placeholderKey)) {
            return null;
        }
        return placeholderKey;
    };
    PlaceholderUtils.isPlaceholderKeyValid = function (placeholderKey) {
        if (!placeholderKey) {
            return false;
        }
        if (!placeholderKey[0].match(firstAllowedLetter)) {
            return false;
        }
        var wordMatch = placeholderKey.match(allowedCharacters);
        if (!wordMatch || wordMatch.length != 1 || wordMatch[0] != placeholderKey) {
            return false;
        }
        if (conditionalPlaceholderReservedWords.includes(placeholderKey)) {
            return false;
        }
        if (listPlaceholderReservedWords.includes(placeholderKey)) {
            return false;
        }
        return true;
    };
    PlaceholderUtils.parsePlaceholderExpressionIfApplicable = function (placeholderText) {
        var category = "general";
        var expression = null;
        var placeholderKey = PlaceholderUtils.getPlaceholderKeyFromText(placeholderText);
        if (conditionalPlaceholderReservedWords.includes(placeholderKey)) {
            category = "condition";
        }
        if (listPlaceholderReservedWords.includes(placeholderKey)) {
            category = "list";
        }
        var expressionTokens = [ConditionalToken.If, ConditionalToken.ElseIf, ListToken.Each];
        expressionTokens.forEach(function (expressionToken) {
            if (placeholderKey === null || placeholderKey === void 0 ? void 0 : placeholderKey.startsWith(expressionToken)) {
                placeholderKey = placeholderKey.replace(expressionToken, "");
                placeholderKey = placeholderKey.trim();
                expression = expressionToken;
                category = expressionToken === ListToken.Each ? "list" : "condition";
            }
        });
        return { category: category, placeholderKey: placeholderKey, expression: expression };
    };
    PlaceholderUtils.sanitizePlaceholderName = function (name) {
        if (!name) {
            return name;
        }
        var i = 0;
        var firstChar = name[i];
        while (i < name.length && !firstChar.match(firstAllowedLetter)) {
            firstChar = name[++i];
        }
        name = name.substr(i);
        name = name.replace(disallowedCharacters, "");
        if (conditionalPlaceholderReservedWords.includes(name)) {
            return "";
        }
        return name;
    };
    /**
     * Determines whether two strings are equivalent in invariant culture while case insensitive
     */
    PlaceholderUtils.invariantCultureCompare = function (a, b) {
        // Suply one locale to make sure the order is the same always
        return "".concat(a).localeCompare("".concat(b), "en-US");
    };
    PlaceholderUtils.shouldPlaceholderBeDisabled = function (placeholder) {
        var _b, _c;
        var source = (_c = (_b = placeholder.binding) === null || _b === void 0 ? void 0 : _b.source) !== null && _c !== void 0 ? _c : "";
        switch (source) {
            case ParameterTypes.customerVoiceAnonymousSurvey:
            case ParameterTypes.customerVoiceNonAnonymousSurvey:
                return true;
            default:
                return false;
        }
    };
    PlaceholderUtils.getNewDefaultPlaceholder = function (defaultPlaceholderKey, existingPlaceholders) {
        var sortedEmptyKeys = Object.keys(existingPlaceholders)
            .filter(function (k) { return k.startsWith(defaultPlaceholderKey); })
            .map(function (k) { return k.replace(defaultPlaceholderKey, ""); })
            .filter(function (k) { return k != null && parseInt(k); })
            .map(function (k) { return parseInt(k); })
            .sort();
        var lastNumber = sortedEmptyKeys.pop() || 0;
        return "".concat(defaultPlaceholderKey).concat(lastNumber + 1);
    };
    PlaceholderUtils.trimPlaceholderKey = function (input) {
        if (!(input === null || input === void 0 ? void 0 : input.trim())) {
            return { trailingSpaces: "", leadingSpaces: "", name: "" };
        }
        var leadingSpacesCount = 0;
        var firstChar = input.charAt(0);
        while (firstChar === " " || firstChar === "\xA0") {
            leadingSpacesCount++;
            firstChar = input.charAt(leadingSpacesCount);
        }
        var trailignSpacesCount = 0;
        var lastChar = input.charAt(input.length - 1);
        while (lastChar === " " || lastChar === "\xA0") {
            trailignSpacesCount++;
            lastChar = input.charAt(input.length - 1 - trailignSpacesCount);
        }
        input = input.substring(leadingSpacesCount, input.length - trailignSpacesCount);
        var sanitizeName = PlaceholderUtils.sanitizePlaceholderName(input);
        return {
            leadingSpaces: PlaceholderUtils.generateSpaces(leadingSpacesCount),
            trailingSpaces: PlaceholderUtils.generateSpaces(trailignSpacesCount),
            name: sanitizeName
        };
    };
    /**
     * Sorts placeholders in invariant culture.
     * This method should be used for placeholders sorting before in-memory saving.
     * @param placeholders Collection of placeholders.
     */
    PlaceholderUtils.sortPlaceholders = function (placeholders) {
        var result = {};
        if (placeholders) {
            // Sort the map
            Object.entries(placeholders)
                .map(function (item) { return item[0]; })
                .sort(function (a, b) { return PlaceholderUtils.invariantCultureCompare(a, b); })
                .forEach(function (value) { return (result[value] = placeholders[value]); });
        }
        return result;
    };
    /**
     * Compares two strings in locale.
     * This method should be used for UI sorting.
     * @param a First string to compare.
     * @param b Second string to compare.
     */
    PlaceholderUtils.compareInLocale = function (a, b) {
        var options = {
            usage: "sort",
            sensitivity: "base",
            caseFirst: "false",
            ignorePunctuation: false
        };
        // Use default locale for now. This should be replaced with locale from CRM
        return "".concat(a).localeCompare("".concat(b), undefined, options);
    };
    PlaceholderUtils.sanitizeInput = function (input) {
        var leadingSpacesCount = "";
        var trailingSpacesCount = "";
        var placeholderName = PlaceholderUtils.getPlaceholderKeyFromPlaceholder(input);
        if (!!placeholderName) {
            input = placeholderName;
        }
        else {
            input = input ? input : PlaceholderToken.EmptyName;
            var splittedName = PlaceholderUtils.trimPlaceholderKey(input);
            input = splittedName.name;
            leadingSpacesCount = splittedName.leadingSpaces;
            trailingSpacesCount = splittedName.trailingSpaces;
        }
        return { leadingSpacesCount: leadingSpacesCount, trailingSpacesCount: trailingSpacesCount, name: input };
    };
    PlaceholderUtils.generatePlaceholderNameFromTemplate = function (template, placeholders) {
        var i = 1;
        var placeholderName = this.sanitizeInput(template(i)).name;
        if (placeholderName.indexOf(i + "") === -1) {
            throw new Error("Please use 'number' argument in template.");
        }
        var existingPlaceholderKeys = Object.keys(placeholders || {});
        if (existingPlaceholderKeys.length === 0) {
            return placeholderName;
        }
        while (placeholders[placeholderName] != null) {
            if (++i >= maxLoops) {
                throw new Error("Max loops reached - please check if template uses 'number' argument.");
            }
            placeholderName = this.sanitizeInput(template(i)).name;
        }
        return placeholderName;
    };
    PlaceholderUtils.createOrGetPlaceholderLabel = function (newPlaceholder, placeholders, template, comparer) {
        var internalComparer = comparer;
        if (internalComparer == null) {
            internalComparer = function (a, b) { return JSON.stringify(a) === JSON.stringify(b); };
        }
        var existingPlaceholder = Object.entries(placeholders || {}).find(function (p) { return internalComparer(p[1], newPlaceholder); });
        if (existingPlaceholder != null) {
            return { placeholder: existingPlaceholder[1], placeholderKey: existingPlaceholder[0] };
        }
        var newName = this.generatePlaceholderNameFromTemplate(template, placeholders);
        var internalNewPlaceholder = __assign(__assign({}, JSON.parse(JSON.stringify(newPlaceholder))), { title: newName });
        return { placeholder: internalNewPlaceholder, placeholderKey: newName };
    };
    PlaceholderUtils.expandSelectionIfApplicable = function (selectionStart, selectionEnd, text) {
        var leftPart;
        var slidingWindowLength = 2;
        var expandedSelectionStart = selectionStart;
        var expandedSelectionEnd = selectionEnd;
        while (selectionStart >= 0) {
            leftPart = text.slice(selectionStart, selectionStart + slidingWindowLength);
            if (leftPart === PlaceholderToken.LeftHandlebars) {
                expandedSelectionStart = selectionStart;
                break;
            }
            if (text.slice(selectionStart - slidingWindowLength, selectionStart) === PlaceholderToken.RightHandlebars) {
                break;
            }
            selectionStart--;
        }
        var rightPart;
        while (selectionEnd <= text.length) {
            rightPart = text.slice(selectionEnd - slidingWindowLength, selectionEnd);
            if (rightPart === PlaceholderToken.RightHandlebars) {
                expandedSelectionEnd = selectionEnd;
                break;
            }
            if (text.slice(selectionEnd, selectionEnd + slidingWindowLength) === PlaceholderToken.LeftHandlebars) {
                break;
            }
            selectionEnd++;
        }
        return { expandedSelectionStart: expandedSelectionStart, expandedSelectionEnd: expandedSelectionEnd };
    };
    PlaceholderUtils.narrowSelectionIfApplicable = function (selectionStart, selectionEnd, text, placeholderToken) {
        var adjustSelectionStart = text.indexOf(placeholderToken, selectionStart);
        var narrowedSelectionStart = selectionStart;
        var narrowedSelectionEnd = selectionEnd;
        if (adjustSelectionStart >= 0) {
            narrowedSelectionStart = adjustSelectionStart;
            narrowedSelectionEnd = adjustSelectionStart + placeholderToken.length;
        }
        return { narrowedSelectionStart: narrowedSelectionStart, narrowedSelectionEnd: narrowedSelectionEnd };
    };
    PlaceholderUtils.generateEachItems = function (properties) {
        var e_1, _b;
        var newProperties = {};
        try {
            for (var _c = __values(Object.keys(properties)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var key = _d.value;
                var oldProperty = properties[key];
                newProperties[key] = PlaceholderUtils.generateEachProperty(oldProperty);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return [
            {
                type: ["object"],
                properties: newProperties
            }
        ];
    };
    PlaceholderUtils.generateEachProperty = function (oldProperty) {
        var _b, _c, _d, _e, _f;
        var outputType = (_b = oldProperty.binding) === null || _b === void 0 ? void 0 : _b.outputType;
        if ((outputType === "picklist" || outputType === "multiSelectPicklist")) {
            var outputFormat = (_d = (_c = oldProperty.binding) === null || _c === void 0 ? void 0 : _c.outputFormat) !== null && _d !== void 0 ? _d : "caption";
            return {
                type: oldProperty.type,
                binding: {
                    source: DataSources.ContextOutputBase,
                    outputPath: (_e = oldProperty.binding) === null || _e === void 0 ? void 0 : _e.outputPath,
                    outputType: outputType,
                    outputFormat: outputFormat
                }
            };
        }
        else {
            return {
                type: oldProperty.type,
                binding: {
                    source: DataSources.ContextOutputBase,
                    outputPath: (_f = oldProperty.binding) === null || _f === void 0 ? void 0 : _f.outputPath,
                    outputType: outputType
                }
            };
        }
    };
    PlaceholderUtils.getPlaceholderToken = function (placeholderKey, placeholderCategory, displayNames) {
        var _b;
        var responsePlaceholderToken = null;
        var displayName = (_b = displayNames === null || displayNames === void 0 ? void 0 : displayNames[placeholderKey]) !== null && _b !== void 0 ? _b : placeholderKey;
        switch (placeholderCategory) {
            case "condition":
                responsePlaceholderToken = "".concat(PlaceholderToken.LeftHandlebars, "#if ").concat(displayName).concat(PlaceholderToken.RightHandlebars);
                break;
            case "list":
                responsePlaceholderToken = "".concat(PlaceholderToken.LeftHandlebars, "#each ").concat(displayName).concat(PlaceholderToken.RightHandlebars);
                break;
            case "general":
            default:
                responsePlaceholderToken = "".concat(PlaceholderToken.LeftHandlebars).concat(displayName).concat(PlaceholderToken.RightHandlebars);
                break;
        }
        return responsePlaceholderToken;
    };
    PlaceholderUtils.getPlaceholderKeyFromText = function (placeholder) {
        var matches = placeholder === null || placeholder === void 0 ? void 0 : placeholder.match(/\{\{.*\}\}/);
        if ((matches === null || matches === void 0 ? void 0 : matches.length) === 1) {
            var placeholderKey = matches[0].replace(PlaceholderToken.LeftHandlebars, "").replace(PlaceholderToken.RightHandlebars, "");
            return placeholderKey;
        }
        return null;
    };
    PlaceholderUtils.isPlaceholderEmpty = function (placeholder) {
        return !((placeholder === null || placeholder === void 0 ? void 0 : placeholder.defaultValue) || (placeholder === null || placeholder === void 0 ? void 0 : placeholder.binding));
    };
    PlaceholderUtils.isPlaceholderInternal = function (placeholder) {
        return placeholder === null || placeholder === void 0 ? void 0 : placeholder.isInternal;
    };
    PlaceholderUtils.isPlaceholderStatic = function (placeholder) {
        var _b, _c, _d;
        return ((_d = (_c = (_b = placeholder === null || placeholder === void 0 ? void 0 : placeholder.binding) === null || _b === void 0 ? void 0 : _b.inputs) === null || _c === void 0 ? void 0 : _c.recordId) === null || _d === void 0 ? void 0 : _d.value) != null;
    };
    PlaceholderUtils.isPlaceholderConstant = function (placeholder) {
        return PlaceholderUtils.isBindingConstant(placeholder === null || placeholder === void 0 ? void 0 : placeholder.binding);
    };
    PlaceholderUtils.getConstantValue = function (placeholder) {
        return PlaceholderUtils.getBindingConstantValue(placeholder === null || placeholder === void 0 ? void 0 : placeholder.binding);
    };
    PlaceholderUtils.isBindingConstant = function (binding) {
        return (binding === null || binding === void 0 ? void 0 : binding.source) == DataSources.Constant;
    };
    PlaceholderUtils.getBindingConstantValue = function (binding) {
        var _b, _c;
        if (!PlaceholderUtils.isBindingConstant(binding)) {
            return null;
        }
        return (_c = (_b = binding === null || binding === void 0 ? void 0 : binding.inputs) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.value;
    };
    PlaceholderUtils.getBindingConstantValueWithType = function (binding) {
        var _b, _c;
        if (!PlaceholderUtils.isBindingConstant(binding)) {
            return null;
        }
        return {
            dataType: binding === null || binding === void 0 ? void 0 : binding.outputType,
            value: (_c = (_b = binding === null || binding === void 0 ? void 0 : binding.inputs) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.value
        };
    };
    PlaceholderUtils.shouldPlaceholdersAttributeBeUpdated = function (definedPlaceholders, placeholders) {
        return Object.keys(definedPlaceholders !== null && definedPlaceholders !== void 0 ? definedPlaceholders : {}).length !== Object.keys(placeholders !== null && placeholders !== void 0 ? placeholders : {}).length;
    };
    PlaceholderUtils.generateSpaces = function (count) {
        var spaces = [];
        for (var i = 0; i < count; i++) {
            spaces.push("\xA0");
        }
        return spaces.join("");
    };
    PlaceholderUtils.getPlaceholderTitle = function (placeholderKey, placeholder) {
        var _b;
        return (_b = placeholder === null || placeholder === void 0 ? void 0 : placeholder.title) !== null && _b !== void 0 ? _b : placeholderKey;
    };
    PlaceholderUtils.placeholderContainsCustomerVoiceBindingSource = function (placeholder) {
        var _b, _c;
        return (placeholder.isInternal &&
            (((_b = placeholder.binding) === null || _b === void 0 ? void 0 : _b.source) === DataSources.NonAnonymousSurveySource ||
                ((_c = placeholder.binding) === null || _c === void 0 ? void 0 : _c.source) === DataSources.AnonymousSurveySource));
    };
    var _a;
    _a = PlaceholderUtils;
    PlaceholderUtils.extractSourceType = function (binding) {
        var _b, _c, _d;
        if (!binding) {
            return "";
        }
        return (_d = (_c = (_b = PlaceholderUtils.extractFirstNonDvDSBinding(binding)) === null || _b === void 0 ? void 0 : _b.inputs) === null || _c === void 0 ? void 0 : _c.sourceType) === null || _d === void 0 ? void 0 : _d.value;
    };
    PlaceholderUtils.isFirstOfManyPlaceholder = function (binding) {
        var _b, _c;
        return ((_c = (_b = binding === null || binding === void 0 ? void 0 : binding.inputs) === null || _b === void 0 ? void 0 : _b.outputMode) === null || _c === void 0 ? void 0 : _c.value) === "firstOfMany";
    };
    PlaceholderUtils.isStaticBinding = function (binding) {
        var extractedBinding = PlaceholderUtils.extractFirstNonDvDSBinding(binding);
        return extractedBinding == null;
    };
    PlaceholderUtils.extractFirstNonDvDSBinding = function (binding) {
        var _b, _c, _d, _e, _f, _g, _h, _j;
        if (binding == null) {
            return null;
        }
        if (binding.source !== DataSources.DataverseDataSource) {
            return binding;
        }
        if ((_c = (_b = binding.inputs) === null || _b === void 0 ? void 0 : _b.recordId) === null || _c === void 0 ? void 0 : _c.binding) {
            return PlaceholderUtils.extractFirstNonDvDSBinding((_e = (_d = binding.inputs) === null || _d === void 0 ? void 0 : _d.recordId) === null || _e === void 0 ? void 0 : _e.binding);
        }
        if ((_g = (_f = binding.inputs) === null || _f === void 0 ? void 0 : _f.relatingRecordId) === null || _g === void 0 ? void 0 : _g.binding) {
            return PlaceholderUtils.extractFirstNonDvDSBinding((_j = (_h = binding.inputs) === null || _h === void 0 ? void 0 : _h.relatingRecordId) === null || _j === void 0 ? void 0 : _j.binding);
        }
        return null;
    };
    PlaceholderUtils.setSpecificRecordToBinding = function (binding, recordId, rootAttribute) {
        if (binding == null) {
            return binding;
        }
        var bindingClone = JSON.parse(JSON.stringify(binding));
        var rootBinding = PlaceholderUtils.getRootBinding(bindingClone);
        if (recordId) {
            rootBinding.source = DataSources.DataverseDataSource;
            rootBinding.inputs = {
                sourceType: {
                    value: rootAttribute.sourceLogicalName
                },
                sourcePrimaryIdName: {
                    value: rootAttribute.sourcePrimaryId
                },
                recordsetType: {
                    value: rootAttribute.sourceSetName
                },
                recordId: {
                    value: recordId
                }
            };
        }
        else {
            rootBinding.inputs = {
                sourceType: {
                    value: rootAttribute.sourceLogicalName
                },
                sourcePrimaryIdName: {
                    value: rootAttribute.sourcePrimaryId
                },
                recordsetType: {
                    value: rootAttribute.sourceSetName
                },
                recordId: {
                    value: undefined
                }
            };
        }
        return bindingClone;
    };
    PlaceholderUtils.getBindingItems = function (binding) {
        // ToDo: move the code here as it does not need to be in TargetEntitiesExtractorService
        var targetEntitiesExtractor = new TargetEntitiesExtractorService();
        return targetEntitiesExtractor.getBindingItems(binding);
    };
    PlaceholderUtils.extractAllPlaceholderBindings = function (placeholders) {
        // ToDo: move the code here as it does not need to be in TargetEntitiesExtractorService
        var targetEntitiesExtractor = new TargetEntitiesExtractorService();
        return targetEntitiesExtractor.extractAllPlaceholderBindings(placeholders);
    };
    PlaceholderUtils.getRootBinding = function (currentBinding) {
        var _b, _c;
        if (currentBinding && currentBinding.source === DataSources.DataverseDataSource && currentBinding.inputs) {
            if ((_b = currentBinding.inputs.recordId) === null || _b === void 0 ? void 0 : _b.binding) {
                return PlaceholderUtils.getRootBinding(currentBinding.inputs.recordId.binding);
            }
            if ((_c = currentBinding.inputs.relatingRecordId) === null || _c === void 0 ? void 0 : _c.binding) {
                return PlaceholderUtils.getRootBinding(currentBinding.inputs.relatingRecordId.binding);
            }
        }
        return currentBinding;
    };
    PlaceholderUtils.getAllBindingLogicalNames = function (binding) {
        var _b, _c, _d, _e, _f, _g, _h;
        var retValue = [];
        retValue.push((_c = (_b = binding === null || binding === void 0 ? void 0 : binding.inputs) === null || _b === void 0 ? void 0 : _b.sourceType) === null || _c === void 0 ? void 0 : _c.value);
        var currentBinding = binding;
        while ((_e = (_d = currentBinding === null || currentBinding === void 0 ? void 0 : currentBinding.inputs) === null || _d === void 0 ? void 0 : _d.recordId) === null || _e === void 0 ? void 0 : _e.binding) {
            currentBinding = (_g = (_f = currentBinding === null || currentBinding === void 0 ? void 0 : currentBinding.inputs) === null || _f === void 0 ? void 0 : _f.recordId) === null || _g === void 0 ? void 0 : _g.binding;
            retValue.push((_h = currentBinding.inputs.sourceType) === null || _h === void 0 ? void 0 : _h.value);
        }
        return retValue;
    };
    PlaceholderUtils.getSelectedPath = function (selectedItems) {
        var _b;
        if (((_b = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) !== null && _b !== void 0 ? _b : 0) === 0) {
            return [];
        }
        var firstSelectedItem = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems[0];
        var currentPathInLoop = [
            (firstSelectedItem === null || firstSelectedItem === void 0 ? void 0 : firstSelectedItem.source) === DataSources.DataverseDataSource
                ? DataSources.DataverseSpecificRecordsSource
                : extractSourceTypeFromBindingItem(firstSelectedItem)
        ];
        var finalPath = __spreadArray(__spreadArray(__spreadArray([], __read(currentPathInLoop), false), [
            firstSelectedItem.sourceType
        ], false), __read(selectedItems.filter(function (k) { return k.output != null; }).reduce(function (a, b) { return __spreadArray(__spreadArray([], __read(a), false), [b.output], false); }, [])), false);
        return finalPath;
    };
    PlaceholderUtils.waitForPlaceholderPropagation = function (timeout) {
        if (timeout === void 0) { timeout = 5000; }
        return __awaiter(void 0, void 0, void 0, function () {
            var waitTime, timeSinceLastDiscovery, timeSinceLastPlaceholderSet, discoveryFinished, isAfterTimeout;
            var _b, _c;
            return __generator(_a, function (_d) {
                switch (_d.label) {
                    case 0:
                        waitTime = 100;
                        _d.label = 1;
                    case 1:
                        if (!true) return [3 /*break*/, 3];
                        timeSinceLastDiscovery = Math.max(Date.now() - Number.parseInt((_b = window === null || window === void 0 ? void 0 : window.sessionStorage) === null || _b === void 0 ? void 0 : _b.getItem("LastPlaceholderDiscovery")) || 0, 0);
                        timeSinceLastPlaceholderSet = Math.max(Date.now() - Number.parseInt((_c = window === null || window === void 0 ? void 0 : window.sessionStorage) === null || _c === void 0 ? void 0 : _c.getItem("LastPlaceholderSet")) || 0, 0);
                        discoveryFinished = timeSinceLastPlaceholderSet >= timeSinceLastDiscovery;
                        isAfterTimeout = timeSinceLastPlaceholderSet > timeout;
                        if (discoveryFinished || isAfterTimeout) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, waitTime); })];
                    case 2:
                        _d.sent();
                        return [3 /*break*/, 1];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    PlaceholderUtils.getRightOperandPlaceholderRootBinding = function (binding) {
        var _b, _c, _d, _e, _f;
        return PlaceholderUtils.getRootBinding((_f = (_e = (_d = (_c = (_b = binding === null || binding === void 0 ? void 0 : binding.inputs) === null || _b === void 0 ? void 0 : _b.filterConditionExpression) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.rightOperand) === null || _e === void 0 ? void 0 : _e.placeholder) === null || _f === void 0 ? void 0 : _f.binding);
    };
    return PlaceholderUtils;
}());
export { PlaceholderUtils };
